import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProjectProvider } from './context/ProjectContext';
import Navbar from './components/Navbar';
import ProjectSidebar from './components/ProjectSidebar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Jobs from './pages/Jobs';
import Admin from './pages/Admin';
import KanbanBoard from './pages/KanbanBoard';
import TaskBoard from './pages/TaskBoard';
import { AgentProvider } from './context/AgentContext';
import PasswordResetModal from './components/PasswordResetModal';
import { auth } from './services/api';
import './App.css';

const PrivateRoute = ({ children, adminOnly = false }) => {
  const { user, loading, logout } = useAuth();
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  useEffect(() => {
    if (user && user.must_reset_password) {
      setShowPasswordReset(true);
    }
  }, [user]);

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to="/tasks" />;
  }

  const handlePasswordResetSuccess = async () => {
    setShowPasswordReset(false);
    // Refresh user data to update must_reset_password flag
    try {
      const response = await auth.getCurrentUser();
      const updatedUser = response.data;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      window.location.reload(); // Reload to update user context
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  if (showPasswordReset) {
    return (
      <PasswordResetModal
        onSuccess={handlePasswordResetSuccess}
        onLogout={logout}
      />
    );
  }

  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <>
      {user && <Navbar />}
      {user && <ProjectSidebar />}
      <div className={user ? "main-content-with-sidebar" : ""}>
        <Routes>
          <Route path="/login" element={user ? <Navigate to="/tasks" /> : <Login />} />
          <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/tasks" element={<PrivateRoute><AgentProvider><TaskBoard /></AgentProvider></PrivateRoute>} />
          <Route path="/kanban" element={<PrivateRoute><KanbanBoard /></PrivateRoute>} />
          <Route path="/jobs" element={<PrivateRoute><Jobs /></PrivateRoute>} />
          <Route path="/admin" element={<PrivateRoute adminOnly><Admin /></PrivateRoute>} />
          <Route path="/" element={<Navigate to={user ? "/tasks" : "/login"} />} />
        </Routes>
      </div>
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <ProjectProvider>
        <Router>
          <div className="App">
            <AppRoutes />
          </div>
        </Router>
      </ProjectProvider>
    </AuthProvider>
  );
}

export default App;
